import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { format, parseISO } from "date-fns";
import { Crown, Gift, CheckCircle2, CalendarDays, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TIMES = Array.from({ length: 22 }, (_, i) => { const h = 8 + Math.floor(i / 2); const m = i % 2 ? '30' : '00'; return `${String(h).padStart(2, '0')}:${m}`; });
const label12 = (t) => { const [h, m] = t.split(':').map(Number); return `${((h + 11) % 12) + 1}:${String(m).padStart(2, '0')} ${h >= 12 ? 'PM' : 'AM'}`; };

export default function RenewalPage() {
  const { token } = useParams();
  const [offer, setOffer] = useState(null);
  const [error, setError] = useState('');
  const [services, setServices] = useState([]);
  const [date, setDate] = useState('');
  const [time, setTime] = useState('10:00');
  const [serviceId, setServiceId] = useState('');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => {
    axios.get(`${API}/public/renewal/${token}`).then(r => setOffer(r.data)).catch(e => setError(e.response?.data?.detail || 'This link is no longer valid'));
    axios.get(`${API}/public/services`).then(r => { const iv = r.data.filter(s => ['iv_drip', 'hydration'].includes(s.category)); setServices(iv); if (iv[0]) setServiceId(iv[0].id); }).catch(() => {});
  }, [token]);

  const submit = async () => {
    if (!date) { setError('Please choose a date'); return; }
    setSubmitting(true); setError('');
    try {
      const r = await axios.post(`${API}/public/renewal/${token}/accept`, { requested_datetime: `${date}T${time}:00`, service_type_id: serviceId || null, notes: notes || null });
      setDone(r.data);
    } catch (e) { setError(e.response?.data?.detail || 'Something went wrong — please call us.'); }
    finally { setSubmitting(false); }
  };

  const Shell = ({ children }) => (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="w-full max-w-lg rounded-2xl border border-primary/40 bg-card/90 p-6 sm:p-8 space-y-5 shadow-2xl" data-testid="renewal-page">
        <p className="text-[10px] uppercase tracking-[0.3em] text-primary flex items-center gap-2"><Crown className="w-4 h-4" />EliteIV · Elite Membership</p>
        {children}
      </div>
    </div>
  );

  if (error && !offer) return <Shell><p className="text-rose-300" data-testid="renewal-error">{error}</p><p className="text-sm text-muted-foreground flex items-center gap-2"><Phone className="w-4 h-4" />Call or text 727-503-2572 and we'll take care of it.</p></Shell>;
  if (!offer) return <Shell><p className="text-muted-foreground text-sm">Loading your offer…</p></Shell>;

  if (done || offer.renewal_requested_at) {
    const apt = done || offer.renewal_appointment;
    return (
      <Shell>
        <div className="text-center space-y-3" data-testid="renewal-success">
          <CheckCircle2 className="w-12 h-12 text-emerald-400 mx-auto" />
          <h1 className="text-2xl font-bold gold-gradient-text brand-heading">You're all set, {offer.first_name}!</h1>
          <p className="text-sm text-muted-foreground">We've received your renewal visit request{apt?.start_datetime ? <> for <b className="text-foreground">{format(parseISO(apt.start_datetime), 'EEEE, MMMM d · h:mm a')}</b></> : ''}{apt?.service ? ` (${apt.service})` : ''}. Our team will confirm shortly, and your $450 renewal is collected at the visit.</p>
        </div>
      </Shell>
    );
  }

  if (offer.already_renewed) return <Shell><p className="text-sm" data-testid="renewal-already">Great news — your membership has already been renewed. See you at your next visit!</p></Shell>;

  return (
    <Shell>
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold gold-gradient-text brand-heading">Thank you for 3 months, {offer.first_name}</h1>
        <p className="text-sm text-muted-foreground mt-2">Your term ended {format(parseISO(offer.end_date), 'MMMM d')}. {offer.bonus_available && <>Your <b className="text-foreground">bonus IV drip + IM injection</b> are ready until {format(parseISO(offer.bonus_expires_at), 'MMMM d')}.</>}</p>
      </div>
      <div className="rounded-xl border border-primary/40 bg-gradient-to-br from-primary/15 to-transparent p-4">
        <div className="flex items-baseline justify-between"><p className="text-xs uppercase tracking-[0.2em] text-primary">Renew · 3 more months</p><p className="text-3xl font-bold gold-gradient-text">${offer.plan.price.toFixed(0)}</p></div>
        <ul className="text-sm mt-3 space-y-1"><li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-primary shrink-0" />1 IV drip every month</li><li className="flex gap-2"><Gift className="w-4 h-4 text-primary shrink-0" />Bonus IV drip + IM injection at the end</li><li className="flex gap-2 text-xs text-muted-foreground"><CalendarDays className="w-4 h-4 shrink-0" />Payment collected at your renewal visit</li></ul>
      </div>
      <div className="space-y-3">
        <p className="text-sm font-medium">Pick your first renewal visit</p>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5"><Label className="text-xs">Date</Label><Input type="date" min={format(new Date(), 'yyyy-MM-dd')} value={date} onChange={e => setDate(e.target.value)} data-testid="renewal-date" /></div>
          <div className="space-y-1.5"><Label className="text-xs">Time</Label>
            <Select value={time} onValueChange={setTime}><SelectTrigger data-testid="renewal-time"><SelectValue /></SelectTrigger><SelectContent>{TIMES.map(t => <SelectItem key={t} value={t}>{label12(t)}</SelectItem>)}</SelectContent></Select>
          </div>
        </div>
        {services.length > 0 && (
          <div className="space-y-1.5"><Label className="text-xs">Which drip this time?</Label>
            <Select value={serviceId} onValueChange={setServiceId}><SelectTrigger data-testid="renewal-service"><SelectValue /></SelectTrigger><SelectContent>{services.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent></Select>
          </div>
        )}
        <div className="space-y-1.5"><Label className="text-xs">Anything we should know? (optional)</Label><Textarea rows={2} value={notes} onChange={e => setNotes(e.target.value)} data-testid="renewal-notes" /></div>
        {error && <p className="text-sm text-rose-300" data-testid="renewal-error">{error}</p>}
        <Button onClick={submit} disabled={submitting} className="w-full h-12 text-base bg-primary text-primary-foreground" data-testid="renewal-submit">
          <Crown className="w-5 h-5 mr-2" />{submitting ? 'Sending…' : 'Book my renewal visit'}
        </Button>
        <p className="text-[11px] text-muted-foreground text-center">Prefer to talk? Call or text 727-503-2572.</p>
      </div>
    </Shell>
  );
}
